import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { DashboardService } from 'src/services/dashboard/dashboard.service';
import { useAuthentication } from 'src/features/authentication/context';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { PartialDashboardType } from './types';

const AllDashboardsContext = createContext<any>(null);

export const AllDashboardsProvider = ({ children }: { children: React.ReactNode }) => {
  const [dashboards, setDashboards] = useState<PartialDashboardType[]>([]);
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const onDeleteDashboard = useCallback(
    async ({ dashboardId, customerId }: { dashboardId: string; customerId: string }) => {
      try {
        await DashboardService.deleteDashboard({
          customerId,
          dashboardId,
        });

        let updated: PartialDashboardType[] = [];
        setDashboards((prev) => {
          updated = prev.filter((d) => d.dashboardId !== dashboardId);
          return updated;
        });

        await Promise.resolve();

        const firstDashboardId = updated?.[0]?.dashboardId;
        const target = firstDashboardId
          ? `/preview-dashboard/${firstDashboardId}?customerId=${customerId}`
          : `/sites-management?customerId=${customerId}`;

        enqueueSnackbar('Successfully deleted dashboard', { variant: 'success' });
        navigate(target);

        return updated;
      } catch (err) {
        enqueueSnackbar('Could not delete dashboard', { variant: 'error' });
        console.error(err);
      }
    },
    [enqueueSnackbar, navigate, setDashboards]
  );

  const addDashboardToState = useCallback((dashboard: any) => {
    setDashboards((prevState: any) => [...prevState, dashboard]);
  }, []);

  return (
    <AllDashboardsContext.Provider
      value={{ dashboards, setDashboards, onDeleteDashboard, addDashboardToState }}
    >
      {children}
    </AllDashboardsContext.Provider>
  );
};

export const useAllDashboardsContext = ({
  title,
  isActive,
}: { title?: string; isActive?: boolean } = {}) => {
  const context = useContext(AllDashboardsContext);
  const { setDashboards } = context ?? {};
  const {
    customerId: { value: customerId },
  } = useAuthentication();

  useEffect(() => {
    const getAllDashboards = async () => {
      const response = await DashboardService.getAll({ customerId: customerId! });
      setDashboards(response || []);
    };

    if (title === 'Dashboards' && isActive && customerId) {
      getAllDashboards();
    }
  }, [title, isActive, customerId, setDashboards]);

  return context;
};
